import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { resolveSignedHalUrl } from '../lib/resolveSignedHalUrl';

type ThumbnailPreviewProps = {
  thumbnailHref: string | null;
  className?: string;
  imageClassName?: string;
};

export default function ThumbnailPreview({
  thumbnailHref,
  className = 'bg-white p-2 rounded-2xl border border-gray-200 shadow-sm flex items-center justify-center min-h-[200px] relative overflow-hidden',
  imageClassName = 'max-w-full max-h-[300px] object-contain rounded-xl',
}: ThumbnailPreviewProps) {
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [isFetching, setIsFetching] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (!thumbnailHref) {
      setSignedUrl(null);
      setIsFetching(false);
      setIsImageLoading(false);
      setHasError(false);
      return;
    }

    let cancelled = false;
    setIsFetching(true);
    setHasError(false);
    setSignedUrl(null);
    setIsImageLoading(false);

    resolveSignedHalUrl(thumbnailHref, 'thumbnailUrl')
      .then((url) => {
        if (cancelled) return;
        if (!url) {
          setHasError(true);
          return;
        }
        setSignedUrl(url);
        setIsImageLoading(true);
      })
      .catch(() => {
        if (!cancelled) {
          setHasError(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsFetching(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [thumbnailHref]);

  if (!thumbnailHref) {
    return (
      <div className={className}>
        <span className="text-xs font-semibold uppercase tracking-wider text-gray-300">
          No preview available
        </span>
      </div>
    );
  }

  if (isFetching || hasError || !signedUrl) {
    return (
      <div className={className}>
        {isFetching ? (
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        ) : (
          <span className="text-xs font-semibold uppercase tracking-wider text-gray-300">
            No preview available
          </span>
        )}
      </div>
    );
  }

  return (
    <div className={className}>
      {isImageLoading && (
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600 absolute" />
      )}
      <img
        src={signedUrl}
        alt="Document preview"
        className={imageClassName}
        onLoad={() => setIsImageLoading(false)}
        onError={() => {
          setIsImageLoading(false);
          setHasError(true);
        }}
      />
    </div>
  );
}
